<template>
  <v-data-table-server
    v-bind="$attrs"
    :headers="headers"
    :item-value="itemValue"
    :density="density"
    :page-text="footerPageText"
    :items="loading ? [] : items"
    :items-length="normalizedItemsLength"
    :loading="loading"
    v-model:page="pageNumber"
    v-model:items-per-page="rowsPerPage"
    v-model:sort-by="sortByItems"
    :items-per-page-options="[10, 25, 50, 100]"
    must-sort
    @update:options="performFetch"
  >
    <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-data-table-server>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, watch, computed } from 'vue';
import { DataTableHeader } from 'vuetify';
import useQueryParam from '@/hooks/useQueryParam';
import sl from '@/serviceLocator';

export interface OareDataTableOptions {
  page: number;
  rows: number;
  sortBy: string;
  sortDesc: boolean;
}

interface SortItem {
  key: string;
  order?: boolean | 'asc' | 'desc';
}

// A server-side data table whose page, row count and sort live in the query
// string. Every option change ends in v-data-table-server emitting
// update:options, and that single event is what triggers a fetch, including
// the first one on mount.
export default defineComponent({
  name: 'OareDataTable',
  inheritAttrs: false,
  props: {
    items: {
      // Rows are plain objects; this is also what Vuetify's DataTableHeader
      // assumes, so headers and items agree on the row type.
      type: Array as PropType<Record<string, any>[]>,
      default: () => [],
    },
    headers: {
      type: Array as PropType<DataTableHeader[]>,
      default: () => [],
    },
    itemValue: {
      type: String,
      default: 'id',
    },
    density: {
      type: String as PropType<'default' | 'comfortable' | 'compact'>,
      default: 'default',
    },
    pageText: {
      type: String,
      default: '$vuetify.dataFooter.pageText',
    },
    itemsLength: {
      type: Number,
      default: 0,
    },
    debounce: {
      type: Boolean,
      default: false,
    },
    defaultSort: {
      type: String,
      default: '',
    },
    defaultDesc: {
      type: Boolean,
      default: true,
    },
    defaultPage: {
      type: Number,
      default: 1,
    },
    defaultRows: {
      type: Number,
      default: 10,
    },
    errorMessage: {
      type: String,
      default: 'Failed to fetch items',
    },
    fetchItems: {
      type: Function as PropType<
        (options: OareDataTableOptions) => Promise<void>
      >,
      required: true,
    },
    watchedParams: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  setup(props) {
    const _ = sl.get('lodash');
    const actions = sl.get('globalActions');
    const sortBy = useQueryParam('sortBy', props.defaultSort, true);
    const sortDesc = useQueryParam('sortDesc', String(props.defaultDesc), true);
    const page = useQueryParam('page', String(props.defaultPage), false);
    const rows = useQueryParam('rows', String(props.defaultRows), true);

    const loading = ref(false);
    // -1 means the server total is still loading. Vuetify 4 computes a zero
    // page from negative totals and emits page 0, which is invalid for OARE's
    // one-based API pagination, so report one page past the current one
    // instead: paging stays enabled and the footer drops the total.
    const totalUnknown = computed(() => props.itemsLength < 0);
    const normalizedItemsLength = computed(() =>
      totalUnknown.value
        ? Number(page.value) * Number(rows.value) + 1
        : props.itemsLength
    );
    const footerPageText = computed(() =>
      totalUnknown.value ? '{0}-{1}' : props.pageText
    );

    const pageNumber = computed({
      get: () => Number(page.value),
      set: value => {
        page.value = String(value);
      },
    });

    const rowsPerPage = computed({
      get: () => Number(rows.value),
      set: value => {
        rows.value = String(value);
      },
    });

    const sortByItems = computed<SortItem[]>({
      get: (): SortItem[] =>
        sortBy.value
          ? [
              {
                key: sortBy.value,
                order: sortDesc.value === 'true' ? 'desc' : 'asc',
              },
            ]
          : [],
      set: (items: SortItem[]) => {
        const [first] = items;
        sortBy.value = first ? first.key : '';
        sortDesc.value = String(first ? first.order === 'desc' : false);
      },
    });

    const tableOptions = computed<OareDataTableOptions>(() => ({
      page: Number(page.value),
      rows: Number(rows.value),
      sortBy: sortBy.value,
      sortDesc: sortDesc.value === 'true',
    }));

    const queryParams = props.watchedParams.map(paramName =>
      useQueryParam(paramName, '', true)
    );

    const performFetch = async () => {
      try {
        loading.value = true;
        await props.fetchItems(tableOptions.value);
      } catch (err) {
        actions.showErrorSnackbar(props.errorMessage, err as Error);
      } finally {
        loading.value = false;
      }
    };

    // A filter change restarts from page 1. If we are already there the page
    // prop does not change, so the table would not emit update:options.
    const queryParamsOnChange = () => {
      if (page.value === '1') {
        performFetch();
      } else {
        page.value = '1';
      }
    };

    watch(
      queryParams,
      props.debounce
        ? _.debounce(queryParamsOnChange, 500)
        : queryParamsOnChange
    );

    return {
      loading,
      normalizedItemsLength,
      footerPageText,
      pageNumber,
      rowsPerPage,
      sortByItems,
      performFetch,
    };
  },
});
</script>
